import { Injectable } from "@angular/core";
import { ComparisonOperator, Field, FieldType, FormDefinition } from "../vforms-model";
import { TextBoxType } from "../field-types/text-box/text-box";
import { SingleSelectionListType } from "../field-types/single-selection-list/single-selection-list";
import { MultipleSelectionListType } from "../field-types/multiple-selection-list/multiple-selection-list";
import { SingleSelectionDropdownType } from "../field-types/single-selection-dropdown/single-selection-dropdown";
import { MultipleSelectionDropdownType } from "../field-types/multiple-selection-dropdown/multiple-selection-dropdown";
import { DatetimeType } from "../field-types/datetime/datetime";
import { TextareaType } from "../field-types/textarea/text-area";
import { NumberFieldType } from "../field-types/number/number";
import { EmailFieldType } from "../field-types/email/email";
import {
    CheckBoxConverter,
    Cid10Converter,
    DatetimeConverter,
    EmailFieldConverter,
    ExpressionEvaluatorBuilder,
    FieldConverter,
    FileUploaderConverter,
    MultipleSelectionDropdownFieldConverter,
    MultipleSelectionListFieldConverter,
    NumberFieldConverter,
    SingleSelectionDropdownFieldConverter,
    SingleSelectionListFieldConverter,
    TextareaConverter,
    TextBoxConverter,
} from "../formly/model";
import { assign } from "lodash-es";
import { CheckBoxType } from "../field-types/check-box/check-box";
import { Cid10Type } from "../field-types/cid10/cid10";
import { FileUploaderType } from "../field-types/file-uploader/file-uploader";
import {
    Predicate,
    Conditional,
    Expression,
    ComparisonExpression,
    FieldAction,
    Action,
} from "../expression-settings/model";
import { FormlyFieldConfig } from "@ngx-formly/core";

@Injectable({
    providedIn: "root",
})
export class BuilderService {
    _fieldTypes: FieldType[];

    _fieldTypesMap: Map<string, FieldType>;

    _fieldConverterMap: Map<string, FieldConverter<Field>>;

    constructor() {
        this._fieldTypes = [
            new Cid10Type(),
            new TextBoxType(),
            new TextareaType(),
            new CheckBoxType(),
            new SingleSelectionListType(),
            new SingleSelectionDropdownType(),
            new MultipleSelectionListType(),
            new MultipleSelectionDropdownType(),
            new NumberFieldType(),
            new DatetimeType(),
            new EmailFieldType(),
            new FileUploaderType(),
        ];
        this._fieldTypesMap = new Map<string, FieldType>();
        this._fieldTypes.map((f) => this._fieldTypesMap.set(f.id, f));
        this._fieldConverterMap = new Map<string, FieldConverter<any>>();
        this._fieldConverterMap.set(TextBoxType.Id, new TextBoxConverter());
        this._fieldConverterMap.set(CheckBoxType.Id, new CheckBoxConverter());
        this._fieldConverterMap.set(TextareaType.Id, new TextareaConverter());
        this._fieldConverterMap.set(NumberFieldType.Id, new NumberFieldConverter());
        this._fieldConverterMap.set(EmailFieldType.Id, new EmailFieldConverter());
        this._fieldConverterMap.set(MultipleSelectionListType.Id, new MultipleSelectionListFieldConverter());
        this._fieldConverterMap.set(SingleSelectionListType.Id, new SingleSelectionListFieldConverter());
        this._fieldConverterMap.set(SingleSelectionDropdownType.Id, new SingleSelectionDropdownFieldConverter());
        this._fieldConverterMap.set(MultipleSelectionDropdownType.Id, new MultipleSelectionDropdownFieldConverter());
        this._fieldConverterMap.set(DatetimeType.Id, new DatetimeConverter());
        this._fieldConverterMap.set(Cid10Type.Id, new Cid10Converter());
        this._fieldConverterMap.set(FileUploaderType.Id, new FileUploaderConverter());
    }

    toFormlyFields(form: FormDefinition) {
        const items = [];
        let expressionEvaluatorBuilder = new ExpressionEvaluatorBuilder(form);
        form.fields.forEach((field) => {
            // HACK: Add * to required fields
            if (field.required && field.label.indexOf("*") === -1) {
                field.label += " *";
            }
            const converter: FieldConverter<Field> = this._fieldConverterMap.get(field.type.id);
            let formlyField = converter.convert(field);
            let visibilityEvaluatorBuilder = expressionEvaluatorBuilder.buildVisibilityEvaluator(field);
            let statusEvaluatorBuilder = expressionEvaluatorBuilder.buildStatusEvaluator(field);

            if (visibilityEvaluatorBuilder) {
                assign(formlyField, {
                    visibilityEvaluatorBuilder: visibilityEvaluatorBuilder,
                    hideExpression: visibilityEvaluatorBuilder.evaluator,
                });
            }

            if (statusEvaluatorBuilder) {
                assign(formlyField, {
                    statusEvaluatorBuilder: statusEvaluatorBuilder,
                    expressionProperties: {
                        "templateOptions.disabled": statusEvaluatorBuilder.evaluator,
                    },
                });
            }

            if (field.type.id !== CheckBoxType.Id) {
                let labelField = {
                    template: `<div class="">${field.label}</div>`,
                } as FormlyFieldConfig;

                if (visibilityEvaluatorBuilder) {
                    assign(labelField, {
                        visibilityEvaluatorBuilder: visibilityEvaluatorBuilder,
                        hideExpression: visibilityEvaluatorBuilder.evaluator,
                    });
                }

                if (statusEvaluatorBuilder) {
                    assign(labelField, {
                        statusEvaluatorBuilder: statusEvaluatorBuilder,
                        expressionProperties: {
                            "templateOptions.disabled": statusEvaluatorBuilder.evaluator,
                        },
                    });
                }

                items.push(labelField);
            }
            items.push(formlyField);
        });
        return items;
    }

    toJson(form: FormDefinition): string {
        const fields = [];
        form.fields.forEach((field) => {
            fields.push(field.toJson());
        });

        const conditionals = [];
        form.conditionals.forEach((conditional) => {
            conditionals.push({
                name: conditional.name,
                predicate: {
                    expressions: conditional.predicate.expressions.map((expression) => {
                        return {
                            conditions: expression.conditions.map((condition) => {
                                return {
                                    field: condition.field?.id,
                                    operator: condition.operator,
                                    value: condition.value,
                                };
                            }),
                        };
                    }),
                },
                positiveActions: conditional.positiveActions.map((fieldAction) => {
                    return {
                        action: fieldAction.action,
                        field: fieldAction.field?.id,
                    };
                }),
                negativeActions: conditional.negativeActions.map((fieldAction) => {
                    return {
                        action: fieldAction.action,
                        field: fieldAction.field?.id,
                    };
                }),
            });
        });

        const definition = {
            title: form.title,
            description: form.description,
            fields,
            conditionals,
        };
        return JSON.stringify(definition);
    }

    fromJson(definition: string): FormDefinition {
        const form = new FormDefinition();

        if (definition) {
            const data = JSON.parse(definition);
            form.type = data.type ?? "vform";
            form.title = data.title;
            form.description = data.description;
            form.excludedFields = data.excludedFields;
            form.requiredDescription = data.requiredDescription;
            if (form.isVForm) {
                data.fields.forEach((f) => {
                    const fieldType: FieldType = this._fieldTypesMap.get(f.typeId);
                    const field = fieldType.createField();
                    assign(field, f);
                    form.fields.push(field);
                });
            }

            if (data.conditionals) {
                form.conditionals = data.conditionals?.map((conditional) => {
                    return assign(new Conditional(), {
                        name: conditional.name,
                        predicate: assign(new Predicate(), {
                            expressions: conditional.predicate.expressions.map((expression) => {
                                return assign(new Expression(), {
                                    conditions: expression.conditions.map((condition) => {
                                        return assign(new ComparisonExpression(), {
                                            field: form.fields.find((f) => f.id === condition.field),
                                            operator: condition.operator as ComparisonOperator,
                                            value: condition.value,
                                        });
                                    }),
                                });
                            }),
                        }),
                        positiveActions: conditional.positiveActions.map((fieldAction) => {
                            return assign(new FieldAction(), {
                                action: fieldAction.action as Action,
                                field: form.fields.find((f) => f.id === fieldAction.field),
                            });
                        }),
                        negativeActions: conditional.negativeActions.map((fieldAction) => {
                            return assign(new FieldAction(), {
                                action: fieldAction.action as Action,
                                field: form.fields.find((f) => f.id === fieldAction.field),
                            });
                        }),
                    });
                });
            }
        }
        return form;
    }
}
